package com.htmlagent.site.i18n

import com.htmlagent.shared.ApiErrorCode
import com.htmlagent.shared.stream.TurnFailureCode
import com.htmlagent.site.api.ApiFailure

/**
 * What the person is told when a call to the API fails.
 *
 * The text lives in `i18n/strings.json` and this only chooses which one: the transport says what
 * happened, the screen says what it means. The `when` over [ApiErrorCode] has no `else`, so a new
 * error on the backend stops this compiling instead of falling through to a generic message.
 */
internal fun ApiFailure.asMessage(): String {
    val t = AppLocale.strings.errors
    return when (this) {
        is ApiFailure.Problem -> problem.code.asMessage()
        is ApiFailure.Unexpected -> t.unexpected
        is ApiFailure.Unreachable -> t.unreachable
        is ApiFailure.Malformed -> t.malformedResponse
    }
}

/** Why a turn ended badly, in the person's language: the stream carries only the reason. */
internal fun TurnFailureCode.asMessage(): String {
    val t = AppLocale.strings.errors
    return when (this) {
        TurnFailureCode.PAGE_CUT_SHORT -> t.pageCutShort
        TurnFailureCode.REPLY_CUT_SHORT -> t.replyCutShort
        TurnFailureCode.NOT_FINISHED -> t.notFinished
        TurnFailureCode.TIMED_OUT -> t.timedOut
        TurnFailureCode.NOT_SAVED -> t.notSaved
        TurnFailureCode.GENERATION_FAILED -> t.generationFailed
    }
}

/**
 * The backend's own errors. Split out from [asMessage] so neither `when` grows past what can be
 * read at a glance; both stay exhaustive, which is the whole point of the pair.
 */
private fun ApiErrorCode.asMessage(): String {
    val t = AppLocale.strings.errors
    return when (this) {
        ApiErrorCode.SESSION_BUSY -> t.sessionBusy
        ApiErrorCode.SESSION_PROTECTED -> t.sessionProtected
        ApiErrorCode.GUEST_LIMIT_REACHED -> t.guestLimitReached
        ApiErrorCode.SESSION_NOT_FOUND -> t.sessionNotFound
        ApiErrorCode.VERSION_NOT_FOUND -> t.versionNotFound
        ApiErrorCode.EMPTY_PROMPT -> t.emptyPrompt
        ApiErrorCode.MESSAGE_NOT_FOUND -> t.messageNotFound
        ApiErrorCode.INVALID_USER_ID -> t.invalidUserId
        ApiErrorCode.STORAGE_FAILURE -> t.storageFailure
        ApiErrorCode.MALFORMED_SESSION_ID -> t.malformedSessionId
        ApiErrorCode.MALFORMED_BODY -> t.malformedBody
        ApiErrorCode.MALFORMED_REQUEST -> t.malformedRequest
        ApiErrorCode.INVALID_IMAGE -> t.invalidImage
        ApiErrorCode.INVALID_EMAIL -> t.invalidEmail
        ApiErrorCode.TOO_MANY_REQUESTS -> t.tooManyRequests
        ApiErrorCode.TOO_MANY_GUESTS -> t.tooManyGuests
        ApiErrorCode.MAIL_UNAVAILABLE -> t.mailUnavailable
        ApiErrorCode.INVALID_LINK -> t.invalidLink
        ApiErrorCode.WRONG_MATCH -> t.wrongMatch
        ApiErrorCode.NOT_A_GUEST -> t.notAGuest
        ApiErrorCode.UNAUTHENTICATED -> t.unauthenticated
    }
}
